// feature-suggestion-dialog.component.ts

import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

import { KnowledgeService } from 'src/app/admin-view/components/knowledge-base/services/knowledge.service';

@Component({
  selector: 'app-feature-suggestion-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule,
  ],
  template: `
    <div class="suggestion-dialog">
      <div class="dialog-header">
        <div class="header-icon">
          <mat-icon>lightbulb</mat-icon>
        </div>
        <div class="header-text">
          <h2>Suggest a Feature</h2>
          <p>Share your ideas with us</p>
        </div>
        <button mat-icon-button class="close-btn" (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="dialog-body">
        <div class="form-field" [class.has-error]="titleTouched && !isTitleValid()">
          <label>Title</label>
          <input type="text" 
                 [(ngModel)]="title" 
                 (blur)="titleTouched = true"
                 placeholder="What's your idea?" 
                 maxlength="100">
          <div class="field-footer">
            <span class="error-message" *ngIf="titleTouched && !isTitleValid()">
              <mat-icon>error_outline</mat-icon>
              Minimum 3 characters required
            </span>
            <span class="char-count" [class.push-right]="!(titleTouched && !isTitleValid())">{{ title.length }}/100</span>
          </div>
        </div>

        <div class="form-field" [class.has-error]="descriptionTouched && !isDescriptionValid()">
          <label>Description</label>
          <textarea [(ngModel)]="description"
                    (blur)="descriptionTouched = true"
                    rows="5" 
                    placeholder="Describe your feature idea..."
                    maxlength="2000"></textarea>
          <div class="field-footer">
            <span class="error-message" *ngIf="descriptionTouched && !isDescriptionValid()">
              <mat-icon>error_outline</mat-icon>
              Minimum 10 characters required
            </span>
            <span class="char-count" [class.push-right]="!(descriptionTouched && !isDescriptionValid())">{{ description.length }}/2000</span>
          </div>
        </div>

        <div class="form-field">
          <label>How important is this to you?</label>
          <div class="priority-options">
            <button *ngFor="let option of priorityOptions"
                    type="button"
                    class="priority-btn"
                    [class.selected]="priority === option.value"
                    [class.priority-low]="option.value === 'low'"
                    [class.priority-medium]="option.value === 'medium'"
                    [class.priority-high]="option.value === 'high'"
                    (click)="priority = option.value">
              <span class="priority-dot" [style.background]="option.color"></span>
              <span class="priority-label">{{ option.label }}</span>
              <mat-icon class="check-icon" *ngIf="priority === option.value">check</mat-icon>
            </button>
          </div>
        </div>
      </div>

      <div class="dialog-footer">
        <button class="btn-cancel" (click)="close()">Cancel</button>
        <button class="btn-submit" [disabled]="!canSubmit() || submitting" (click)="submit()">
          <mat-spinner diameter="16" *ngIf="submitting"></mat-spinner>
          <span *ngIf="!submitting">Submit</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .suggestion-dialog {
      width: 420px;
      max-width: 100%;
      background: var(--suggestion-bg, #ffffff);
      overflow: hidden;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    }

    .dialog-header {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 20px 24px;
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
      color: white;
    }

    .dialog-header .header-icon {
      width: 44px;
      height: 44px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .dialog-header .header-icon mat-icon {
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    .dialog-header .header-text {
      flex: 1;
    }

    .dialog-header .header-text h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    .dialog-header .header-text p {
      margin: 4px 0 0 0;
      font-size: 13px;
      opacity: 0.9;
    }

    .dialog-header .close-btn {
      color: white;
      opacity: 0.8;
    }

    .dialog-header .close-btn:hover {
      opacity: 1;
      background: rgba(255, 255, 255, 0.15);
    }

    .dialog-body {
      padding: 24px;
      background: var(--suggestion-bg, #ffffff);
    }

    .form-field {
      margin-bottom: 24px;
      position: relative;
    }

    .form-field:last-child {
      margin-bottom: 0;
    }

    .form-field label {
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
      color: var(--suggestion-label, #374151);
    }

    .form-field input,
    .form-field textarea {
      width: 100%;
      padding: 12px 14px;
      border: 1px solid var(--suggestion-input-border, #e2e8f0);
      border-radius: 10px;
      font-size: 14px;
      color: var(--suggestion-input-text, #0f172a);
      background: var(--suggestion-input-bg, #f8fafc);
      box-sizing: border-box;
      font-family: inherit;
    }

    .form-field input:focus,
    .form-field textarea:focus {
      outline: none;
      border-color: #8b5cf6;
      background: var(--suggestion-input-focus-bg, #ffffff);
      box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
    }

    .form-field input::placeholder,
    .form-field textarea::placeholder {
      color: var(--suggestion-placeholder, #94a3b8);
    }

    .form-field textarea {
      resize: vertical;
      min-height: 120px;
    }

    /* Error state */
    .form-field.has-error input,
    .form-field.has-error textarea {
      border-color: #ef4444;
      background: var(--suggestion-input-error-bg, #fef2f2);
    }

    .form-field.has-error input:focus,
    .form-field.has-error textarea:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
    }

    /* Field footer with error and char count */
    .field-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 6px;
      min-height: 18px;
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #ef4444;

      mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
      }
    }

    .char-count {
      font-size: 11px;
      color: var(--suggestion-char-count, #94a3b8);
    }

    .char-count.push-right {
      margin-left: auto;
    }

    /* Priority Options */
    .priority-options {
      display: flex;
      gap: 10px;
    }

    .priority-btn {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 12px;
      border: 2px solid var(--suggestion-btn-border, #e2e8f0);
      border-radius: 10px;
      background: var(--suggestion-btn-bg, #f8fafc);
      font-size: 13px;
      font-weight: 500;
      color: var(--suggestion-btn-text, #64748b);
      cursor: pointer;
      position: relative;
    }

    .priority-btn:hover {
      background: var(--suggestion-btn-hover-bg, #f1f5f9);
      border-color: var(--suggestion-btn-hover-border, #cbd5e1);
    }

    .priority-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .priority-label {
      flex: 1;
      text-align: left;
    }

    .check-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: inherit;
    }

    /* Selected states with specific colors */
    .priority-btn.selected {
      color: var(--suggestion-btn-selected-text, #0f172a);
      font-weight: 600;
    }

    /* Low priority - gray/slate */
    .priority-btn.priority-low.selected {
      background: rgba(148, 163, 184, 0.15);
      border-color: #94a3b8;
      
      .check-icon {
        color: #64748b;
      }
    }

    /* Medium priority - amber/orange */
    .priority-btn.priority-medium.selected {
      background: rgba(245, 158, 11, 0.15);
      border-color: #f59e0b;
      
      .check-icon {
        color: #d97706;
      }
    }

    /* High priority - red */
    .priority-btn.priority-high.selected {
      background: rgba(239, 68, 68, 0.15);
      border-color: #ef4444;
      
      .check-icon {
        color: #dc2626;
      }
    }

    .dialog-footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 16px 24px 20px;
      background: var(--suggestion-footer-bg, #f8fafc);
      border-top: 1px solid var(--suggestion-footer-border, #e2e8f0);
    }

    .dialog-footer .btn-cancel {
      padding: 10px 20px;
      border: 1px solid var(--suggestion-cancel-border, #e2e8f0);
      border-radius: 8px;
      background: var(--suggestion-cancel-bg, #ffffff);
      color: var(--suggestion-cancel-text, #64748b);
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
    }

    .dialog-footer .btn-cancel:hover {
      background: var(--suggestion-cancel-hover-bg, #f1f5f9);
      border-color: var(--suggestion-cancel-hover-border, #cbd5e1);
    }

    .dialog-footer .btn-submit {
      padding: 10px 24px;
      border: none;
      border-radius: 8px;
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
      color: white;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 100px;
    }

    .dialog-footer .btn-submit:hover:not(:disabled) {
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
    }

    .dialog-footer .btn-submit:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .dialog-footer .btn-submit mat-spinner {
      display: inline-block;
    }

    .dialog-footer .btn-submit ::ng-deep circle {
      stroke: white;
    }

    // =======================================================================
    // DARK THEME
    // =======================================================================
    :host-context(body.dark-theme) {
      .suggestion-dialog {
        background: #2a2a2a;
      }

      .dialog-header {
        background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
      }

      .dialog-body {
        background: #2a2a2a;
      }

      .form-field label {
        color: #e5e5e5;
      }

      .form-field input,
      .form-field textarea {
        background: #333333;
        border-color: #4a4a4a;
        color: #f5f5f5;
      }

      .form-field input:focus,
      .form-field textarea:focus {
        background: #383838;
        border-color: #8b5cf6;
        box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
      }

      .form-field input::placeholder,
      .form-field textarea::placeholder {
        color: #707070;
      }

      /* Error state - dark theme */
      .form-field.has-error input,
      .form-field.has-error textarea {
        border-color: #ef4444;
        background: rgba(239, 68, 68, 0.1);
      }

      .form-field.has-error input:focus,
      .form-field.has-error textarea:focus {
        border-color: #ef4444;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
      }

      .error-message {
        color: #f87171;
      }

      .char-count {
        color: #707070;
      }

      /* Priority buttons - dark theme */
      .priority-btn {
        background: #333333;
        border-color: #4a4a4a;
        color: #b0b0b0;
      }

      .priority-btn:hover {
        background: #3a3a3a;
        border-color: #5a5a5a;
      }

      .priority-btn.selected {
        color: #f5f5f5;
      }

      /* Low priority - dark theme */
      .priority-btn.priority-low.selected {
        background: rgba(148, 163, 184, 0.2);
        border-color: #94a3b8;
        
        .check-icon {
          color: #94a3b8;
        }
      }

      /* Medium priority - dark theme */
      .priority-btn.priority-medium.selected {
        background: rgba(245, 158, 11, 0.2);
        border-color: #f59e0b;
        
        .check-icon {
          color: #fbbf24;
        }
      }

      /* High priority - dark theme */
      .priority-btn.priority-high.selected {
        background: rgba(239, 68, 68, 0.2);
        border-color: #ef4444;
        
        .check-icon {
          color: #f87171;
        }
      }

      .dialog-footer {
        background: #333333;
        border-color: #4a4a4a;
      }

      .dialog-footer .btn-cancel {
        background: #3a3a3a;
        border-color: #4a4a4a;
        color: #b0b0b0;
      }

      .dialog-footer .btn-cancel:hover {
        background: #444444;
        border-color: #5a5a5a;
      }
    }
  `],
})
export class FeatureSuggestionDialogComponent {
  title = '';
  description = '';
  priority: 'low' | 'medium' | 'high' = 'medium';
  submitting = false;

  // Touch state for validation
  titleTouched = false;
  descriptionTouched = false;

  priorityOptions = [
    { value: 'low' as const, label: 'Nice to have', color: '#94a3b8' },
    { value: 'medium' as const, label: 'Would help', color: '#f59e0b' },
    { value: 'high' as const, label: 'Really need', color: '#ef4444' },
  ];

  constructor(
    private dialogRef: MatDialogRef<FeatureSuggestionDialogComponent>,
    private knowledgeService: KnowledgeService,
    private snackBar: MatSnackBar,
  ) {}

  isTitleValid(): boolean {
    return this.title.trim().length >= 3;
  }

  isDescriptionValid(): boolean {
    return this.description.trim().length >= 10;
  }

  canSubmit(): boolean {
    return this.isTitleValid() && this.isDescriptionValid();
  }

  async submit() {
    // Mark all fields as touched to show errors
    this.titleTouched = true;
    this.descriptionTouched = true;

    if (!this.canSubmit() || this.submitting) return;

    this.submitting = true;

    try {
      await firstValueFrom(
        this.knowledgeService.submitSuggestion({
          title: this.title.trim(),
          description: this.description.trim(),
          priority: this.priority,
        })
      );

      this.snackBar.open('Thank you! Your suggestion has been submitted.', 'Close', {
        duration: 4000,
      });

      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      this.snackBar.open('Failed to submit. Please try again.', 'Close', {
        duration: 4000,
      });
    }

    this.submitting = false;
  }

  close() {
    this.dialogRef.close(false);
  }
}